import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Alert,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showTwitterNotice, setShowTwitterNotice] = useState<boolean>(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const location = useLocation();

  // Check if we're on a tweets page
  const isTwitterPage = location.pathname.includes('/tweets');

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'News', path: '/news' },
    { name: 'Experts', path: '/experts' },
    { name: 'Market Sentiment', path: '/sentiment' },
    { name: 'Resources Hub', path: '/sources' }
  ];

  // Mobile drawer menu
  const mobileDrawer = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={toggleMobileMenu}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': { 
          width: 240,
          backgroundColor: 'secondary.main',
          color: 'white'
        },
      }}
    >
      <Box sx={{ textAlign: 'center', py: 2 }}>
        {/* Making logo identical to header logo */}
        <Stack direction="row" alignItems="center" justifyContent="center">
          {/* Left Candlesticks image */}
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/assets/images/candlesticks.png`}
            alt="Candlesticks"
            sx={{ 
              height: 30, 
              width: 'auto',
              mr: 0.75
            }}
          />
          {/* Text logo - First part */}
          <Typography 
            component={RouterLink} 
            to="/" 
            sx={{ 
              color: 'primary.main', 
              textDecoration: 'none', 
              fontWeight: 700, 
              fontSize: '1.8rem',
              lineHeight: 1,
              display: 'inline'
            }}
            onClick={toggleMobileMenu}
          >
            XAU
          </Typography>
          {/* Text logo - Second part (silver, smaller, same line) */}
          <Typography
            component={RouterLink}
            to="/"
            sx={{ 
              color: '#c0c0c0', // Silver color
              textDecoration: 'none', 
              fontWeight: 600, 
              fontSize: '1.2rem',
              lineHeight: 1,
              letterSpacing: 1,
              display: 'inline',
              ml: 0.5,
              mb: 0.5 // Slight bottom margin to align better
            }}
            onClick={toggleMobileMenu}
          >
            Journal
          </Typography>
        </Stack>
        <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.2)' }} />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.name} disablePadding>
              <ListItemButton 
                component={RouterLink} 
                to={item.path} 
                onClick={toggleMobileMenu}
                sx={{ 
                  textAlign: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  },
                  ...(location.pathname === item.path && {
                    backgroundColor: 'rgba(255,255,255,0.15)'
                  })
                }}
              >
                <ListItemText primary={item.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" color="secondary">
        <Toolbar sx={{ maxWidth: 'var(--max-width, 1400px)', width: '100%', margin: '0 auto', padding: { xs: '0 1rem', md: '0 2rem' } }}>
          {/* Logo area with images */}
          <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }}>
            {/* Left Candlesticks image */}
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/assets/images/candlesticks.png`}
              alt="Candlesticks"
              sx={{ 
                height: 30, 
                width: 'auto',
                mr: 0.75 // Increased from 0.5 to 0.75
              }}
            />
            {/* Text logo - First part */}
            <Typography 
              component={RouterLink} 
              to="/" 
              sx={{ 
                color: 'primary.main', 
                textDecoration: 'none', 
                fontWeight: 700, 
                fontSize: '1.8rem',
                lineHeight: 1,
                display: 'inline'
              }}
            >
              XAU
            </Typography>
            {/* Text logo - Second part (silver, smaller, same line) */}
            <Typography
              component={RouterLink}
              to="/"
              sx={{ 
                color: '#c0c0c0', // Silver color
                textDecoration: 'none', 
                fontWeight: 600, 
                fontSize: '1.2rem',
                lineHeight: 1,
                letterSpacing: 1,
                display: 'inline',
                ml: 0.5,
                mb: 0.5 // Slight bottom margin to align better
              }}
            >
              Journal
            </Typography>
          </Stack>
          
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            <Button component={RouterLink} to="/" color="inherit">Home</Button>
            <Button component={RouterLink} to="/news" color="inherit">News</Button>
            <Button component={RouterLink} to="/experts" color="inherit">Experts</Button>
            <Button component={RouterLink} to="/sentiment" color="inherit">Market Sentiment</Button>
            <Button component={RouterLink} to="/sources" color="inherit">Resources Hub</Button>
          </Box>
          {/* Hamburger menu icon for mobile */}
          <IconButton 
            edge="end" 
            color="inherit" 
            aria-label="menu"
            onClick={toggleMobileMenu}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile menu drawer */}
      {mobileDrawer}

      <Container component="main" sx={{ flex: 1, py: 4, maxWidth: 'var(--max-width, 1400px) !important' }}>
        {isTwitterPage && showTwitterNotice && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setShowTwitterNotice(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            X (formerly Twitter) posts may not display correctly due to platform limitations. For the most reliable experience, we recommend visiting the expert's profile directly.
          </Alert>
        )}
        {children}
      </Container>

      <Box component="footer" sx={{ bgcolor: 'secondary.main', color: 'rgba(255, 255, 255, 0.7)', p: 2, textAlign: 'center' }}>
        <Typography variant="body2">
          &copy; {new Date().getFullYear()} XAU Journal - Your source for Gold and USD market insights
        </Typography>
      </Box>
    </Box>
  );
};

export default Layout;